import React, { useEffect, useContext, useState, useMemo } from 'react';
import { AppContext } from '../../../../context/Context';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Slider, IconButton, Button, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import '../../../../assets/css/buttons.css';
import FreeImg from '../../../../assets/img/free.png';

const LETTERS = ['B', 'I', 'N', 'G', 'O'];

// ---- Kiosk dark-glass tokens ----
const COLOR = {
  text: '#ffffff',
  muted: 'rgba(255,255,255,0.6)',
  border: 'rgba(255,255,255,0.14)',
  panel: 'rgba(255,255,255,0.06)',
  cellIdle: 'rgba(255,255,255,0.06)',
  cellFree: 'rgba(255,255,255,0.03)',
  daub: '#2fbf71', // matched ball
  current: '#f97316', // latest rop drawn
  gold: '#FFC727', // winning ball / premium prize tier
  win: '#34d399', // prize within limit
  miss: '#ff6b6b', // completed but past the prize limit
};

// Builds the printed 5x5 layout: row-major, center cell is FREE.
function buildGrid(cardNumbers = []) {
  const grid = [];
  let idx = 0;
  for (let row = 0; row < 5; row++) {
    const cells = [];
    for (let col = 0; col < 5; col++) {
      if (row === 2 && col === 2) {
        cells.push(null); // free space
      } else {
        cells.push(cardNumbers[idx++]);
      }
    }
    grid.push(cells);
  }
  return grid;
}

function NumberCell({ value, state }) {
  if (value == null) {
    return (
      <Box
        sx={{
          aspectRatio: '1 / 1',
          borderRadius: '10px',
          backgroundColor: COLOR.cellFree,
          border: `1px solid ${COLOR.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={FreeImg}
          alt="Free"
          sx={{ width: '78%', height: '78%', objectFit: 'contain', opacity: 0.9 }}
        />
      </Box>
    );
  }

  const daubColor =
    state === 'winning'
      ? COLOR.gold
      : state === 'current'
        ? COLOR.current
        : state === 'matched'
          ? COLOR.daub
          : null;

  // Dark numbers read better on the bright gold daub.
  const numberColor = daubColor
    ? state === 'winning'
      ? '#1a1206'
      : '#ffffff'
    : COLOR.text;

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: '10px',
        backgroundColor: COLOR.cellIdle,
        border: `1px solid ${COLOR.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {daubColor && (
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            backgroundColor: daubColor,
            boxShadow:
              state === 'current' || state === 'winning'
                ? `0 0 16px ${daubColor}`
                : 'none',
            transition: 'all 120ms ease',
          }}
        />
      )}
      <Typography
        sx={{
          position: 'relative',
          fontSize: '30px',
          fontWeight: 800,
          color: numberColor,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ModalPapirCheckCard() {
  const { t } = useTranslation();

  const {
    updateFocusOnMainScreen,
    updateShowModalPapirCheckCard,
    papirCardInfo,
    updatePapirCardInfo,
    papirScannedInfo,
    showModalPapirGameFinishedWithoutPrize,
  } = useContext(AppContext);

  const [cardIndex, setCardIndex] = useState(0);
  const [currentRop, setCurrentRop] = useState(0);
  const [playing, setPlaying] = useState(false);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const cards = useMemo(
    () => (Array.isArray(papirCardInfo?.cards) ? papirCardInfo.cards : []),
    [papirCardInfo]
  );

  const card = cards[cardIndex] || null;

  // Draw numbers sorted by call order (rop).
  const drawNumbers = useMemo(() => {
    const nums = papirCardInfo?.draw?.numbers;
    if (!Array.isArray(nums)) return [];
    return [...nums].sort((a, b) => a.rop - b.rop);
  }, [papirCardInfo]);

  const fullDrawMax = drawNumbers.length
    ? drawNumbers[drawNumbers.length - 1].rop
    : 0;

  // The authoritative result rop comes from the winning payload — the card's
  // own prize* fields are only "would win here" rops before the cutoff rules
  // are applied. Cards with no bingo win fall back to the full draw length.
  const winningBingo = papirCardInfo?.winning?.prizeBingo?.[0] || null;
  const winningRop = winningBingo?.rop > 0 ? winningBingo.rop : fullDrawMax;

  // Start each card at its final result, then let the user rewind.
  useEffect(() => {
    setCurrentRop(winningRop);
    setPlaying(false);
  }, [cardIndex, winningRop]);

  // Auto-replay: step forward one rop at a time until the winning ball.
  useEffect(() => {
    if (!playing) return undefined;
    if (currentRop >= winningRop) {
      setPlaying(false);
      return undefined;
    }
    const id = setTimeout(() => {
      setCurrentRop((r) => Math.min(r + 1, winningRop));
    }, 650);
    return () => clearTimeout(id);
  }, [playing, currentRop, winningRop]);

  const calledBalls = useMemo(
    () => drawNumbers.filter((n) => n.rop <= currentRop),
    [drawNumbers, currentRop]
  );

  const calledSet = useMemo(
    () => new Set(calledBalls.map((n) => n.ball)),
    [calledBalls]
  );

  const currentEntry = drawNumbers.find((n) => n.rop === currentRop) || null;
  const currentBall = currentEntry?.ball ?? null;
  const atResult = currentRop >= winningRop;
  // Only highlight the gold winning ball / trophy when THIS card actually won
  // the full bingo (the game-wide array may hold another card's win).
  const cardWonBingo = (papirCardInfo?.winning?.prizeBingo || []).some(
    (p) => Number(p.cardNumber) === Number(card?.cardNumber)
  );
  const isWin = atResult && cardWonBingo;

  const grid = useMemo(() => buildGrid(card?.cardNumbers), [card]);

  const cellState = (value) => {
    if (value == null) return 'free';
    if (value === currentBall) {
      return isWin ? 'winning' : 'current';
    }
    if (calledSet.has(value)) return 'matched';
    return 'idle';
  };

  const matchCount = useMemo(() => {
    if (!card?.cardNumbers) return 0;
    return card.cardNumbers.filter((n) => calledSet.has(n)).length;
  }, [card, calledSet]);

  const handleClose = () => {
    // Return to the finished-game modal underneath; it owns the payout flow.
    updateShowModalPapirCheckCard(false);
    updatePapirCardInfo(false);
  };

  const stepTo = (rop) => {
    setPlaying(false);
    setCurrentRop(Math.max(1, Math.min(rop, winningRop)));
  };

  const togglePlay = () => {
    if (atResult) {
      setCurrentRop(1);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };

  const gameId =
    papirCardInfo?.winning?.gameID ?? card?.gameID ?? papirCardInfo?.gameID;

  if (!papirCardInfo || !card) {
    return null;
  }

  // Last few rop for the running strip (most recent first).
  const recentBalls = calledBalls.slice(-10).reverse();

  // Game plan carries the game name and the prize-limit balls (rop1 / rop2)
  // per pattern. We compare each card's hypothetical completion rop against
  // those limits so the player sees whether (and at which tier) it qualifies.
  const gameplan = papirCardInfo?.gameplan || null;
  const gameName = gameplan?.name || card.bingoName;

  const buildPattern = (label, cardRop, plan) => {
    const limits = plan ? [plan.rop1, plan.rop2].filter((r) => r > 0) : [];
    const limitLow = limits.length ? Math.min(...limits) : null; // premium tier
    const limitHigh = limits.length ? Math.max(...limits) : null; // last chance
    const hasRop = cardRop > 0;

    let tier = 'none'; // none | premium | prize | missed
    if (hasRop && limitHigh != null) {
      if (cardRop <= limitLow) tier = 'premium';
      else if (cardRop <= limitHigh) tier = 'prize';
      else tier = 'missed';
    }

    return { label, cardRop, limitLow, limitHigh, hasRop, tier };
  };

  const patterns = [
    buildPattern(
      t('sales.papirCheckCardPatternBilde'),
      card.prizeBilde,
      gameplan?.bilde
    ),
    buildPattern(
      t('sales.papirCheckCardPatternRamme'),
      card.prizeRamme,
      gameplan?.ramme
    ),
    buildPattern(
      t('sales.papirCheckCardPatternBingo'),
      card.prizeBingo,
      gameplan?.bingo
    ),
  ];

  // Actual prizes for the scanned card — only known when we arrived from the
  // "with prize" modal. The "without prize" modal tells us the card won nothing.
  const fromWithoutPrize = showModalPapirGameFinishedWithoutPrize === true;
  const prizes = Array.isArray(papirScannedInfo?.prizes)
    ? papirScannedInfo.prizes
    : [];
  const totalWin = papirScannedInfo?.win;

  // Timeline marks only for patterns that actually landed a prize.
  const marks = (() => {
    if (fromWithoutPrize) return [];
    const seen = new Set();
    return patterns
      .filter((p) => p.tier === 'premium' || p.tier === 'prize')
      .map((p) => ({ value: p.cardRop, label: p.label }))
      .filter((m) => m.value > 0 && m.value <= winningRop)
      .filter((m) => {
        if (seen.has(m.value)) return false;
        seen.add(m.value);
        return true;
      });
  })();

  return (
    <div id="react-confirm-alert">
      <div
        className="react-confirm-alert-overlay"
        style={{
          background: 'rgba(3,6,18,0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 'min(1120px, 96vw)',
            maxHeight: '94vh',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 30px 90px rgba(0,0,0,0.65)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            background:
              'linear-gradient(135deg, rgba(18,26,55,0.98), rgba(10,14,28,0.98))',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              minHeight: 70,
              px: '24px',
              py: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                sx={{ color: 'white', fontSize: '24px', fontWeight: 900, lineHeight: 1.1 }}
              >
                {t('sales.papirCheckCardTitle')}
                {gameName ? ` · ${gameName}` : ''}
              </Typography>
              <Typography sx={{ color: COLOR.muted, fontSize: 15, mt: 0.5 }}>
                {t('sales.papirCheckCardCard')} #{card.cardNumber}
                {gameId != null && (
                  <>
                    {'  ·  '}
                    {t('sales.papirCheckCardGame')}: {gameId}
                  </>
                )}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: COLOR.muted }}>
              <CloseIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ p: '24px', overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                alignItems: 'stretch',
              }}
            >
              {/* ---- The bingo card ---- */}
              <Box sx={{ flex: '1 1 420px', minWidth: 340 }}>
                {cards.length > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        setCardIndex((i) => (i - 1 + cards.length) % cards.length)
                      }
                      sx={{ color: COLOR.gold }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 16, color: COLOR.muted }}>
                      {t('sales.papirCheckCardCard')} {cardIndex + 1} /{' '}
                      {cards.length}
                    </Typography>
                    <IconButton
                      onClick={() => setCardIndex((i) => (i + 1) % cards.length)}
                      sx={{ color: COLOR.gold }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                )}

                {/* BINGO header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px',
                    mb: '8px',
                  }}
                >
                  {LETTERS.map((l) => (
                    <Box
                      key={l}
                      sx={{
                        backgroundColor: 'rgba(255,199,39,0.12)',
                        border: `1px solid ${COLOR.border}`,
                        borderRadius: '10px',
                        py: 1,
                        textAlign: 'center',
                        fontWeight: 900,
                        fontSize: '26px',
                        color: COLOR.gold,
                        letterSpacing: '0.05em',
                      }}
                    >
                      {l}
                    </Box>
                  ))}
                </Box>

                {/* 5x5 grid */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '8px',
                  }}
                >
                  {grid.map((row, r) =>
                    row.map((value, c) => (
                      <NumberCell
                        key={`${r}-${c}`}
                        value={value}
                        state={cellState(value)}
                      />
                    ))
                  )}
                </Box>
              </Box>

              {/* ---- Replay side panel ---- */}
              <Box
                sx={{
                  flex: '1 1 300px',
                  minWidth: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {/* Current rop badge */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: COLOR.panel,
                    border: `1px solid ${COLOR.border}`,
                    borderRadius: '14px',
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: isWin ? COLOR.gold : COLOR.current,
                      boxShadow: `0 0 16px ${isWin ? COLOR.gold : COLOR.current}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '30px',
                      fontWeight: 800,
                      color: isWin ? '#1a1206' : '#fff',
                    }}
                  >
                    {currentBall ?? '—'}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, color: COLOR.muted }}>
                      {isWin
                        ? t('sales.papirCheckCardWinningBall')
                        : t('sales.papirCheckCardLastBall')}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 700, color: COLOR.text }}
                    >
                      {t('sales.papirCheckCardBallNumber')} {currentRop} /{' '}
                      {winningRop}
                    </Typography>
                    {isWin && (
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: COLOR.gold,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.25,
                          fontWeight: 800,
                        }}
                      >
                        <EmojiEventsIcon sx={{ fontSize: 18 }} /> BINGO
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Matches */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 15,
                    color: COLOR.muted,
                    px: 0.5,
                  }}
                >
                  <span>
                    {t('sales.papirCheckCardDrawnBalls')}: {calledBalls.length}
                  </span>
                  <span>
                    {t('sales.papirCheckCardMatches')}:{' '}
                    <strong style={{ color: COLOR.win }}>{matchCount}</strong> /
                    24
                  </span>
                </Box>

                {/* Recent rop strip */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {recentBalls.map((n, i) => (
                    <Box
                      key={n.rop}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        fontWeight: 700,
                        backgroundColor: i === 0 ? COLOR.current : COLOR.cellIdle,
                        color: i === 0 ? '#fff' : COLOR.text,
                        border: `1px solid ${
                          i === 0 ? COLOR.current : COLOR.border
                        }`,
                      }}
                    >
                      {n.ball}
                    </Box>
                  ))}
                </Box>

                {/* Transport controls */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Tooltip title={t('sales.papirCheckCardStart')}>
                    <span>
                      <IconButton
                        onClick={() => stepTo(1)}
                        disabled={currentRop <= 1}
                        sx={{ color: COLOR.text }}
                      >
                        <FirstPageIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('sales.papirCheckCardPrevious')}>
                    <span>
                      <IconButton
                        onClick={() => stepTo(currentRop - 1)}
                        disabled={currentRop <= 1}
                        sx={{ color: COLOR.text }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    onClick={togglePlay}
                    sx={{
                      color: '#1a1206',
                      backgroundColor: COLOR.gold,
                      width: 56,
                      height: 56,
                      '&:hover': { backgroundColor: '#FFD54F' },
                    }}
                  >
                    {playing ? (
                      <PauseIcon sx={{ fontSize: 30 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 30 }} />
                    )}
                  </IconButton>
                  <Tooltip title={t('sales.papirCheckCardNext')}>
                    <span>
                      <IconButton
                        onClick={() => stepTo(currentRop + 1)}
                        disabled={atResult}
                        sx={{ color: COLOR.text }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('sales.papirCheckCardResult')}>
                    <span>
                      <IconButton
                        onClick={() => stepTo(winningRop)}
                        disabled={atResult}
                        sx={{ color: COLOR.text }}
                      >
                        <LastPageIcon sx={{ fontSize: 30 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {/* Timeline slider */}
                <Box sx={{ px: 1 }}>
                  <Slider
                    value={Math.min(currentRop, winningRop)}
                    min={1}
                    max={Math.max(winningRop, 1)}
                    marks={marks}
                    onChange={(e, v) => stepTo(v)}
                    valueLabelDisplay="auto"
                    sx={{
                      color: COLOR.gold,
                      '& .MuiSlider-rail': { color: COLOR.border, opacity: 1 },
                      '& .MuiSlider-mark': {
                        backgroundColor: COLOR.win,
                        height: 12,
                        width: 2,
                      },
                      '& .MuiSlider-markLabel': {
                        color: COLOR.muted,
                        fontSize: 11,
                      },
                    }}
                  />
                </Box>

                {/* Card summary */}
                <Box
                  sx={{
                    border: `1px solid ${COLOR.border}`,
                    borderRadius: '14px',
                    backgroundColor: COLOR.panel,
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: COLOR.muted,
                      mb: 0.5,
                    }}
                  >
                    {t('sales.papirCheckCardSummary')}
                  </Typography>
                  {patterns.map((p) => {
                    const ropColor =
                      p.tier === 'premium'
                        ? COLOR.gold
                        : p.tier === 'prize'
                          ? COLOR.win
                          : p.tier === 'missed'
                            ? COLOR.miss
                            : COLOR.muted;
                    return (
                      <Box
                        key={p.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          py: 0.5,
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 15, color: COLOR.text }}>
                          {p.label}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 0.75,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 15, fontWeight: 700, color: ropColor }}
                          >
                            {p.hasRop
                              ? `${t('sales.papirCheckCardBallNumber')} ${p.cardRop}`
                              : '-'}
                          </Typography>
                          {p.limitLow != null && (
                            <Typography sx={{ fontSize: 12, color: COLOR.muted }}>
                              {t('sales.papirCheckCardLimit')} {p.limitLow}/
                              {p.limitHigh}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Actual prize outcome */}
                  <Box
                    sx={{
                      borderTop: `1px solid ${COLOR.border}`,
                      mt: 1,
                      pt: 1,
                    }}
                  >
                    {fromWithoutPrize || prizes.length === 0 ? (
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 800,
                          color: COLOR.muted,
                          textAlign: 'center',
                          py: 0.5,
                        }}
                      >
                        {t('sales.papirCheckCardNoPrize')}
                      </Typography>
                    ) : (
                      <>
                        {prizes.map(({ name, amount }, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 0.5,
                            }}
                          >
                            <Typography sx={{ fontSize: 15, color: COLOR.text }}>
                              {name}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 15, fontWeight: 700, color: COLOR.win }}
                            >
                              {amount} kr
                            </Typography>
                          </Box>
                        ))}
                        {totalWin != null && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mt: 0.5,
                              pt: 0.5,
                              borderTop: `1px dashed ${COLOR.border}`,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: 15, fontWeight: 700, color: COLOR.text }}
                            >
                              {t('sales.papirCheckCardTotal')}
                            </Typography>
                            <Typography
                              sx={{ fontSize: 17, fontWeight: 800, color: COLOR.gold }}
                            >
                              {totalWin} kr
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ display: 'flex', mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleClose();
                }}
                startIcon={<CloseIcon sx={{ fontSize: 28 }} />}
                sx={{
                  height: 72,
                  borderRadius: '16px',
                  bgcolor: '#FFC727',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: 22,
                  textTransform: 'uppercase',
                  boxShadow: '0 12px 32px rgba(255,199,39,0.4)',
                  '&:hover': {
                    bgcolor: '#FFD54F',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 16px 40px rgba(255,199,39,0.55)',
                  },
                  '&:active': { transform: 'translateY(0)' },
                }}
              >
                {t('sales.close')}
              </Button>
            </Box>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default ModalPapirCheckCard;
